/* eslint-disable react/prop-types */
/**
 * ID54/ID96 — Residual-risk threshold picker. Renders the Prioritisation Matrix (consequence ×
 * plausibility) read-only; clicking a cell selects that cell's RATING as the alert level and
 * highlights that cell + every cell of equal-or-greater severity. The signpost then alerts when
 * the risk's residual rating reaches the chosen level or worse.
 */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const SEV = { VL: 1, L: 2, M: 3, H: 4, E: 5 };
const RATING_COLORS = {
  VL: "#c8e6c9",
  L: "#388e3c",
  M: "#fdd835",
  H: "#ef6c00",
  E: "#c62828",
};
const RATING_LABEL = { VL: "Very Low", L: "Low", M: "Medium", H: "High", E: "Extreme" };

export default function ResidualThresholdMatrixDialog({ open, onClose, matrix, value, onSelect }) {
  const cols = matrix?.columns || [];
  const rows = matrix?.rows || [];
  const cellRating = (row, colId) => {
    const c = (row.cells || []).find((x) => x.colId === colId);
    return c?.rating || "";
  };
  const selSev = value ? SEV[value] || 0 : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ color: "#52009b", fontWeight: 700 }}>
        Select residual-risk alert level
      </DialogTitle>
      <DialogContent dividers>
        {cols.length === 0 || rows.length === 0 ? (
          <MDTypography variant="body2" color="text.secondary">
            No prioritisation matrix configured. Set it up under Appetite Settings → Risk Evaluation
            Criteria.
          </MDTypography>
        ) : (
          <>
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              Click a cell — that level <strong>and every more-severe level</strong> are selected.
              You&apos;ll be alerted when the residual rating reaches the chosen level or worse.
            </MDTypography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ tableLayout: "fixed", minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#ede7f6", fontWeight: 700, color: "#52009b" }}>
                      {matrix?.yLabel || "Plausibility"} \ Consequence
                    </TableCell>
                    {cols.map((c) => (
                      <TableCell
                        key={c.id}
                        align="center"
                        sx={{ bgcolor: "#ede7f6", fontWeight: 700, color: "#52009b" }}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: "#f5f5f5" }}>
                        {row.label}
                      </TableCell>
                      {cols.map((c) => {
                        const rating = cellRating(row, c.id);
                        const sev = SEV[rating] || 0;
                        const inRange = sev > 0 && selSev > 0 && sev >= selSev;
                        return (
                          <TableCell
                            key={c.id}
                            align="center"
                            onClick={() => rating && onSelect(rating)}
                            sx={{
                              cursor: rating ? "pointer" : "default",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              color: rating ? "#1c1c1c" : "#bbb",
                              bgcolor: rating ? RATING_COLORS[rating] : "#fafafa",
                              opacity: !selSev || inRange ? 1 : 0.35,
                              border: inRange ? "2px solid #1c1c1c" : "1px solid rgba(0,0,0,0.12)",
                              "&:hover": rating ? { outline: "2px solid #52009b" } : {},
                            }}
                          >
                            {rating || "—"}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {value && (
              <MDTypography variant="body2" sx={{ mt: 1.5, fontWeight: 600, color: "#52009b" }}>
                Alert level: <strong>{RATING_LABEL[value] || value}</strong> or worse
              </MDTypography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={onClose}>Done</MDButton>
      </DialogActions>
    </Dialog>
  );
}
